import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { api, imgSrc } from "@/lib/api";
import { useSeo, TESTIMONIALS, FAQS, SERVICE_LEVELS } from "@/lib/site";
import { MaskLines, Reveal, ChapterHeading } from "@/components/Reveal";
import Marquee from "@/components/Marquee";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/fb2f1d1a-8208-4d6f-a747-f9401f6a5033/images/9094accf6a88be686bff40fc23f0dfcf33d98c0ba7e889c213fdf74b7541c687.jpeg";

export default function Home() {
  useSeo("Breeze Craft — Breeze Block Supply & Installation, Sydney-Wide",
    "Architectural breeze blocks supplied and installed across Sydney. Feature walls, pool screens, front fences. Get a fast free quote.");
  const [blocks, setBlocks] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-16%", "0%"]);

  useEffect(() => {
    api.get("/blocks").then((r) => setBlocks(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-14 sm:pt-24 pb-16 sm:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8">
          <div className="lg:col-span-7 flex flex-col justify-center">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.15, duration: 0.6 }}
              className="kicker mb-6"
              data-testid="hero-kicker"
            >
              Sydney-wide — Supply & Install
            </motion.p>
            <h1 className="font-display font-medium tracking-tight leading-[1.02] text-ink text-5xl sm:text-7xl lg:text-[5.5rem]" data-testid="hero-heading">
              <MaskLines lines={["Breeze blocks,", "built", "beautiful."]} />
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.7 }}
              className="mt-6 text-base sm:text-lg text-neutral-600 leading-relaxed max-w-xl"
            >
              We supply and install architectural breeze blocks across Greater Sydney — feature walls,
              pool screens and front fences that turn light and shadow into the best material on your block.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.7 }}
              className="mt-9 flex flex-wrap gap-4"
            >
              <Link
                to="/quote"
                data-testid="hero-get-quote-button"
                className="inline-flex items-center gap-2 bg-terracotta text-white px-7 py-3.5 text-sm font-semibold hover:bg-terracotta-dark transition-colors duration-300"
              >
                Get a Quote <ArrowRight size={16} />
              </Link>
              <Link
                to="/catalogue"
                data-testid="hero-view-catalogue-button"
                className="inline-flex items-center gap-2 border border-ink px-7 py-3.5 text-sm font-medium text-ink hover:bg-ink hover:text-bone transition-colors duration-300"
              >
                View Catalogue
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9, duration: 0.8 }}
              className="mt-12 grid grid-cols-2 gap-6 max-w-sm"
              data-testid="hero-stats"
            >
              {[["20+", "years in trade"], ["Supply", "+ install"]].map(([n, l]) => (
                <div key={l}>
                  <p className="font-display text-2xl sm:text-3xl font-bold text-ink">{n}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mt-1">{l}</p>
                </div>
              ))}
            </motion.div>
          </div>
          <div className="lg:col-span-5">
            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ duration: 1.1, ease: [0.25, 0.1, 0.25, 1], delay: 0.3 }}
              className="relative aspect-[4/5] overflow-hidden"
              data-testid="hero-image-frame"
            >
              <motion.img
                src={HERO_IMG}
                alt="Flower Breeze block wall overlooking the ocean"
                style={{ y: imgY }}
                className="absolute inset-0 w-full h-[120%] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee items={["Bondi", "Mosman", "Manly", "Surry Hills", "Paddington", "Cronulla", "Marrickville", "Parramatta", "Australian-made Breeze Blocks", "Supply + Install", "Free quotes Sydney-wide"]} />

      {/* 01 CATALOGUE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-24 sm:py-32" data-testid="home-catalogue-section">
        <ChapterHeading number="01" title="Patterns we install" />
        <Reveal delay={0.1} className="mt-4 max-w-xl">
          <p className="text-neutral-600 leading-relaxed">Eight Australian-made breeze block patterns, sourced from leading manufacturers and installed by our own crews across Sydney.</p>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
          {blocks.map((b, i) => (
            <Reveal key={b.slug} delay={i * 0.08}>
              <Link to={`/catalogue/${b.slug}`} data-testid={`home-block-card-${b.slug}`} className="group block bg-bone p-5">
                <div className="aspect-square overflow-hidden">
                  <img src={imgSrc(b.images[0])} alt={b.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="mt-4 flex items-start justify-between">
                  <div>
                    <p className="font-display text-xl font-medium text-ink">{b.name}</p>
                    <p className="font-mono text-[11px] text-neutral-500 mt-1">{b.dimensions}</p>
                  </div>
                  <ArrowUpRight size={18} className="text-neutral-400 group-hover:text-terracotta transition-colors" />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-8">
          <Link to="/catalogue" data-testid="home-catalogue-cta" className="inline-flex items-center gap-2 text-sm font-medium text-terracotta hover:text-terracotta-dark transition-colors">
            Browse the full catalogue <ArrowRight size={16} />
          </Link>
        </Reveal>
      </section>

      {/* 02 SERVICES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-24 sm:py-32" data-testid="home-services-section">
        <ChapterHeading number="02" title="Three ways to work with us" />
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
          {SERVICE_LEVELS.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.08} className="bg-bone p-8">
              <p className="font-mono text-xs text-terracotta tracking-[0.25em]">0{i + 1}</p>
              <h3 className="mt-3 font-display text-2xl font-medium text-ink">{s.label}</h3>
              <p className="mt-3 text-sm text-neutral-600 leading-relaxed">{s.blurb}</p>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-8">
          <Link to="/services" data-testid="home-services-cta" className="inline-flex items-center gap-2 text-sm font-medium text-terracotta hover:text-terracotta-dark transition-colors">
            Compare services <ArrowRight size={16} />
          </Link>
        </Reveal>
      </section>

      {/* TRUST */}
      <section className="border-y border-ink/10 bg-bone-dark" data-testid="home-trust-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {[["Licenced", "NSW contractor licence"], ["Insured", "$20M public liability"], ["20+ years", "in the masonry trade"], ["Sydney-wide", "supply & install"]].map(([t, s], i) => (
            <Reveal key={t} delay={i * 0.06}>
              <p className="font-display text-xl font-bold text-ink">{t}</p>
              <p className="mt-1 text-sm text-neutral-600">{s}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-24 sm:py-32" data-testid="home-testimonials-section">
        <ChapterHeading number="03" title="Word on the street" />
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-10">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.08}>
              <p className="text-lg text-ink leading-relaxed font-display">“{t.quote}”</p>
              <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500">{t.name} · {t.role}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ TEASER */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pb-24 sm:pb-32" data-testid="home-faq-teaser">
        <ChapterHeading number="04" title="Straight answers" />
        <div className="mt-10 divide-y divide-ink/10 border-y border-ink/10">
          {FAQS.slice(0, 3).map((f) => (
            <Reveal key={f.q}>
              <div className="py-6">
                <p className="font-display text-lg font-medium text-ink">{f.q}</p>
                <p className="mt-2 text-sm text-neutral-600 leading-relaxed max-w-3xl">{f.a}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-8">
          <Link to="/faq" data-testid="home-faq-cta" className="inline-flex items-center gap-2 text-sm font-medium text-terracotta hover:text-terracotta-dark transition-colors">
            All FAQs <ArrowRight size={16} />
          </Link>
        </Reveal>
      </section>

      {/* CTA BAND */}
      <section className="bg-terracotta text-white relative grain" data-testid="home-cta-band">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 sm:py-28 text-center">
          <Reveal>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight">Tell us about your wall.</h2>
            <p className="mt-4 text-white/80 max-w-xl mx-auto leading-relaxed">
              Five minutes in the quote form — photos welcome — and we'll come back within one business day.
            </p>
            <Link
              to="/quote"
              data-testid="cta-band-quote-button"
              className="mt-8 inline-flex items-center gap-2 bg-ink text-bone px-8 py-4 text-sm font-semibold hover:bg-bone hover:text-ink transition-colors duration-300"
            >
              Start your quote <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
